/**
 * Where a ClubSnapshot comes from.
 *
 * - Native shell: the `swimset:club:snapshot` CustomEvent (C5). Nothing renders
 *   until the first one lands; a bad body is warned, never rendered.
 * - Browser / snapshot harness (`?clubState=…`, no shell): the C5 fixtures in
 *   ./fixtures, picked by the same dev state the rest of the Club tab uses.
 *
 * Same seam rule as clubDevState.ts: on a device the real message always wins.
 */
import type { ClubDevState } from "../clubDevState";
import { CLUB_SNAPSHOT_EVENT, formatSnapshotDrop, parseClubSnapshot, type ClubSnapshot } from "./snapshot";
import { warnToNative } from "../../bridge/client";
import twoMembers from "./fixtures/snapshot-two-members.json";
import empty from "./fixtures/snapshot-empty.json";
import { calendarClubSnapshotBody } from "./fixtures/calendarClub";

/**
 * The fixture each dev state stands in for. Returns `null` (no strip) for
 * `noClub`, and for a `null` state: that is the native shell (clubDevState
 * ignores `?clubState=` there), which shows nothing until the first native
 * snapshot. Starting a device from the two-member fixture showed a demo
 * clubmate that never went away for a member with no real clubmates.
 */
export function devSnapshotFor(state: ClubDevState | null): ClubSnapshot | null {
  if (state === null || state === "noClub") return null;
  const raw = state === "leaderWaiting" ? empty : state === "calendarClub" ? calendarClubSnapshotBody() : twoMembers;
  const parsed = parseClubSnapshot(raw);
  if (!parsed.ok) {
    console.warn("[club] fixture snapshot rejected", parsed.error);
    return null;
  }
  return parsed.snapshot;
}

/** Subscribe to native snapshots. Returns the unsubscribe.
 *  Replays the body native stashed on `window.__swimsetClubSnapshot` first: an emit
 *  that landed before this subscriber mounted is otherwise lost until the next one,
 *  and the strip never renders (B-010). */
export type SnapshotLoadState = "loading" | "ready" | "error";

/** One mounted document's initial state and recovery. Push updates and request
 * replies use the same parser. A push arriving during a request wins over that
 * request's older reply; disposal invalidates every outstanding callback. */
export function connectClubSnapshot(
  onSnapshot: (s: ClubSnapshot) => void,
  options: {
    request?: () => Promise<unknown>;
    onState?: (state: SnapshotLoadState) => void;
    timeoutMs?: number;
  } = {},
): { retry: () => Promise<void>; dispose: () => void } {
  let disposed = false;
  let received = false;
  let pushGeneration = 0;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const clearDeadline = () => { clearTimeout(timer); timer = undefined; };
  const fail = () => {
    if (!disposed && !received) options.onState?.("error");
  };
  const begin = () => {
    clearDeadline();
    if (!received) {
      options.onState?.("loading");
      timer = setTimeout(fail, options.timeoutMs ?? 5_000);
    }
  };
  // Build 15b ("it kinda seems to refresh pretty continuously"): native re-sends the whole club on
  // every roster bump and every foreground, each stamped with a fresh generatedAt. A club equal to
  // the last one THIS subscriber applied is not applied again: no state write, no repaint. The key
  // leaves generatedAt out (a device's "now" is the live clock, strip.snapshotNow).
  let lastKey: string | null = null;
  const deliver = (detail: unknown) => {
    const parsed = parseClubSnapshot(detail);
    if (!parsed.ok) {
      warnToNative("[club] club:snapshot rejected", parsed.error);
      return false;
    }
    received = true;
    clearDeadline();
    options.onState?.("ready");
    const { generatedAt: _stamp, ...club } = parsed.snapshot;
    const key = JSON.stringify(club);
    if (key === lastKey) return true;
    lastKey = key;
    // A bad element is left out and named; the rest of the club still renders (B-017). The
    // line also reaches the phone's log through the `log` bridge (build 12).
    for (const d of parsed.dropped) warnToNative(formatSnapshotDrop(d));
    onSnapshot(parsed.snapshot);
    return true;
  };
  const handler = (e: Event) => {
    if (deliver((e as CustomEvent).detail)) pushGeneration += 1;
  };
  begin();
  window.addEventListener(CLUB_SNAPSHOT_EVENT, handler);
  const stashed = (window as unknown as { __swimsetClubSnapshot?: unknown }).__swimsetClubSnapshot;
  if (stashed !== undefined) deliver(stashed);
  return {
    async retry() {
      if (disposed || !options.request) return;
      const ticket = ++attempt;
      const beforePush = pushGeneration;
      begin();
      try {
        const body = await options.request();
        if (disposed || ticket !== attempt || beforePush !== pushGeneration) return;
        if (!deliver(body)) fail();
      } catch {
        if (!disposed && ticket === attempt && beforePush === pushGeneration) fail();
      }
    },
    dispose() {
      disposed = true;
      attempt += 1;
      clearDeadline();
      window.removeEventListener(CLUB_SNAPSHOT_EVENT, handler);
    },
  };
}

/** Compatibility wrapper for consumers needing only the event subscription. */
export function subscribeClubSnapshot(onSnapshot: (s: ClubSnapshot) => void): () => void {
  return connectClubSnapshot(onSnapshot).dispose;
}
